import { forwardRef, useImperativeHandle, useState } from 'react';

import { Button } from '@dynatrace/strato-components/buttons';
import { CalendarIcon } from '@dynatrace/strato-icons';

import type { DateSelectorProps, DateSelectorRef } from './types.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useOverlayWithTrigger } from '../../overlays/hooks/useOverlayWithTrigger.js';
import { Overlay } from '../../overlays/overlay/Overlay.js';
import { globalSprinkles } from '../../styles/safe-sprinkles.js';
import { Calendar } from '../calendar/Calendar.js';
import { FormFieldMessageContext } from '../forms-core/contexts/FormFieldMessageContext.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';

/**
 * Calendar widget trigger and overlay component.
 */
export const DateSelector = forwardRef<DateSelectorRef, DateSelectorProps>(
  (props, forwardedRef) => {
    const {
      value,
      onChange,
      disabled,
      error,
      readOnly,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'aria-disabled': ariaDisabledProp,
      min,
      max,
      precision,
      ...remainingProps
    } = props;
    const [open, setOpen] = useState(false);

    const {
      targetRef,
      triggerRef,
      overlayRef,
      overlayState,
      overlayProps,
      overlayTriggerProps,
    } = useOverlayWithTrigger({
      placement: 'bottom-end',
      offset: 8,
      trapFocus: false,
      shouldCloseOnBlur: false,
      open,
      onOpenChange: setOpen,
      // the overlay should not open when disabled or readOnly
      disabled: disabled || readOnly,
    });

    const rootRef = useMergeRefs<HTMLButtonElement>([triggerRef, targetRef]);

    useImperativeHandle(
      forwardedRef,
      (): DateSelectorRef => ({
        open: () => setOpen(true),
        close: () => setOpen(false),
      }),
      [],
    );

    const { state: formFieldState } = useFormFieldMessagesContext();

    return (
      <>
        <Button
          ref={rootRef}
          data-testid={dataTestId}
          className={consumerClassName}
          aria-disabled={ariaDisabledProp}
          style={consumerStyle}
          {...remainingProps}
          {...overlayTriggerProps}
          size="condensed"
          color={error ? 'critical' : 'neutral'}
        >
          <Button.Prefix>
            <CalendarIcon />
          </Button.Prefix>
        </Button>
        <Overlay
          ref={overlayRef}
          isOpen={overlayState.isOpen}
          className={globalSprinkles({
            paddingLeft: 16,
            paddingTop: 16,
            paddingRight: 16,
            paddingBottom: 16,
          })}
          overlayProps={overlayProps}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
        >
          {/* The Calendar should not consume any validation context to not dispatch any errors. We still want the errorstate to adjust the day highlighting. */}
          <FormFieldMessageContext.Provider
            value={{
              state: {
                customMessages: [],
                formControlMessages: [],
                showError: formFieldState.showError,
              },
            }}
          >
            <Calendar
              value={value}
              aria-disabled={ariaDisabledProp}
              min={min}
              max={max}
              onChange={(newIsoDate) => {
                if (newIsoDate) {
                  overlayState.close();
                  return onChange(newIsoDate);
                }
              }}
              precision={precision}
            />
          </FormFieldMessageContext.Provider>
        </Overlay>
      </>
    );
  },
);
